/**
 * CalendarToggleItem Component
 *
 * Displays a device calendar with a toggle switch for linking/unlinking
 * to a family member.
 */

import { StyleSheet, Switch, Text, View } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { Colors, ModernPalette } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';

type CalendarToggleItemProps = {
  calendar: DeviceCalendar;
  isLinked: boolean;
  onToggle: (calendarId: string) => void;
  disabled?: boolean;
};

export function CalendarToggleItem({
  calendar,
  isLinked,
  onToggle,
  disabled = false,
}: CalendarToggleItemProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const scale = useSharedValue(1);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const handleToggle = () => {
    scale.value = withTiming(0.98, { duration: 50 }, () => {
      scale.value = withTiming(1, { duration: 100 });
    });
    onToggle(calendar.id);
  };

  return (
    <Animated.View style={[styles.container, animatedStyle]}>
      <View style={styles.calendarInfo}>
        <View style={[styles.colorDot, { backgroundColor: calendar.color }]} />
        <View style={styles.textContainer}>
          <Text
            style={[styles.title, { color: palette.text, fontFamily: getBodyFont() }]}
            numberOfLines={1}
          >
            {calendar.title}
          </Text>
          {calendar.source?.name && (
            <Text
              style={[styles.source, { color: palette.textMuted, fontFamily: getBodyFont() }]}
              numberOfLines={1}
            >
              {calendar.source.name}
            </Text>
          )}
        </View>
      </View>
      <Switch
        value={isLinked}
        onValueChange={handleToggle}
        trackColor={{
          true: palette.primary,
          false: ModernPalette.borderLight,
        }}
        thumbColor={ModernPalette.white}
        disabled={disabled}
      />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md + 2, // 14px
  },
  calendarInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: Spacing.md,
    marginRight: Spacing.md,
  },
  colorDot: {
    width: 16,
    height: 16,
    borderRadius: 8, // circular
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontSize: 16,
    fontWeight: '500',
  },
  source: {
    fontSize: 12,
    marginTop: Spacing.xxs,
  },
});
